import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import ShareModal from '../../components/ShareModal';
import { getMentorShareUrl, getBookingShareUrl, getReferralCode, getMentorEmbedCode, getMenteeBadgeCode, getReferralUrl, getShareMessages } from '../../utils/referral';
import { referralService } from '../../services';
import { MENTORS } from '../../data/mockData';

function CopyBox({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={value} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 12, color: '#374151', background: '#f9fafb', outline: 'none' }} onClick={e => e.target.select()} />
        <button onClick={copy} style={{ padding: '10px 16px', background: copied ? '#1DB954' : '#1a1a2e', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
          {copied ? '✅ Copied!' : '📋 Copy'}
        </button>
      </div>
      {hint && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function StatCard({ icon, label, value, color = '#1DB954' }) {
  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 24, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Sub-component: social post card (needs own state) ────────
function SocialPostCard({ platform, icon, color, text, url }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 20, border: '1.5px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ background: color + '15', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${color}20` }}>
        <span style={{ fontWeight: 700, fontSize: 14, color }}>{icon} {platform}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ padding: '6px 12px', background: copied ? '#1DB954' : '#fff', color: copied ? '#fff' : '#374151', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            {copied ? '✅ Copied' : '📋 Copy'}
          </button>
          <button onClick={() => window.open(url, '_blank')} style={{ padding: '6px 12px', background: color, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Post Now →
          </button>
        </div>
      </div>
      <div style={{ padding: 16, fontSize: 13, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{text}</div>
    </div>
  );
}

// ── Sub-component: code block with copy ──────────────────────
function CodeBlock({ label, code }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>{label}</div>
      <div style={{ position: 'relative' }}>
        <pre style={{ background: '#1a1a2e', color: '#a5f3fc', padding: 14, borderRadius: 10, fontSize: 11, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{code}</pre>
        <button onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ position: 'absolute', top: 8, right: 8, padding: '4px 10px', background: copied ? '#1DB954' : 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
          {copied ? '✅' : '📋 Copy'}
        </button>
      </div>
    </div>
  );
}

const TIERS = {
  silver:   { label: 'Silver',   icon: '🥈', min: 0,    max: 999,  color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', next: 'Gold',     nextMin: 1000 },
  gold:     { label: 'Gold',     icon: '🥇', min: 1000, max: 2499, color: '#d97706', bg: '#fffbeb', border: '#fde68a', next: 'Platinum', nextMin: 2500 },
  platinum: { label: 'Platinum', icon: '💎', min: 2500, max: null, color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', next: null,       nextMin: null },
};

export default function MentorSharePage() {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [activeSection, setActiveSection] = useState('links');
  const [shareStats, setShareStats] = useState({ totalClicks: 0, thisWeekClicks: 0, conversionRate: 0, bySource: {}, totalReferrals: 0, referralEarned: 0 });
  const [pointsData, setPointsData] = useState(null);

  React.useEffect(() => {
    referralService.getShareStats().then(res => setShareStats(res)).catch(() => {});
    referralService.getMyPoints().then(res => setPointsData(res)).catch(() => {});
  }, []);

  // Get mentor data — match by slug, then by name, then fall back to
  // a user-derived object so we never show another mentor's profile.
  const userSlug = user?.mentorSlug ||
    user?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') ||
    `mentor-${user?.id}`;

  const mentor = MENTORS.find(m => m.slug === user?.mentorSlug) ||
    MENTORS.find(m => m.name === user?.name) || {
      slug:        userSlug,
      name:        user?.name        || 'Mentor',
      initials:    user?.initials    || (user?.name?.split(' ').map(n => n[0]).join('') || 'M'),
      avatarBg:    user?.avatarBg    || '1DB954',
      role:        user?.headline    || 'Tech Mentor',
      company:     user?.company     || '',
      rating:      '—',
      reviewCount: 0,
      priceINR:    0,
      experience:  user?.experience  || '',
    };
  const refCode = getReferralCode(user?.id);
  const profileUrl = getMentorShareUrl(mentor.slug, refCode);
  const bookingUrl = getBookingShareUrl(mentor.slug, '', refCode);
  const referralUrl = getReferralUrl(refCode);
  const messages = getShareMessages(mentor, profileUrl);

  const sections = [
    { id: 'links',   label: '🔗 Share Links' },
    { id: 'social',  label: '📲 Social Posts' },
    { id: 'embed',   label: '🖥️ Embed Widget' },
    { id: 'badge',   label: '🏷️ Badge' },
    { id: 'stats',   label: '📊 Stats' },
  ];

  const embedCode  = getMentorEmbedCode(mentor, { theme: 'light', size: 'medium' });
  const badgeCode  = getMenteeBadgeCode(user, refCode);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e)', borderRadius: 16, padding: '24px 28px', marginBottom: 24, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Tier badge */}
          {pointsData && (() => {
            const tier = TIERS[pointsData.tier] || TIERS.silver;
            return (
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: tier.bg, border: `2.5px solid ${tier.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', flexShrink: 0 }}>
                {tier.icon}
              </div>
            );
          })()}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>📤 Share &amp; Grow</h2>
              {pointsData && (() => {
                const tier = TIERS[pointsData.tier] || TIERS.silver;
                return (
                  <span style={{ padding: '2px 9px', borderRadius: 100, background: tier.color + '30', color: tier.color, fontSize: 11, fontWeight: 800, border: `1px solid ${tier.color}50` }}>
                    {tier.icon} {tier.label}
                  </span>
                );
              })()}
            </div>
            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 13 }}>
              Share your profile to get more bookings
              {pointsData && <span style={{ color: TIERS[pointsData.tier]?.color || '#9ca3af' }}> · {pointsData.totalPoints.toLocaleString()} pts</span>}
            </p>
          </div>
        </div>
        <button onClick={() => setShowModal(true)} style={{ background: '#1DB954', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          📤 Quick Share
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard icon="👥" label="Profile Views"    value={shareStats.totalClicks.toLocaleString()} color="#3b82f6" />
        <StatCard icon="🔗" label="Link Clicks"      value={shareStats.thisWeekClicks} color="#8b5cf6" />
        <StatCard icon="🎁" label="Referrals"        value={shareStats.totalReferrals} color="#f59e0b" />
        <StatCard icon="💰" label="Referral Earned"  value={`₹${shareStats.referralEarned.toLocaleString()}`} color="#1DB954" />
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
            padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: activeSection === s.id ? '#1a1a2e' : '#f3f4f6',
            color:      activeSection === s.id ? '#fff'    : '#374151',
          }}>{s.label}</button>
        ))}
      </div>

      {/* ── SHARE LINKS ─────────────────────────────────────── */}
      {activeSection === 'links' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1a1a2e' }}>🔗 Your Shareable Links</h3>
          <CopyBox label="📋 Public Profile URL" value={profileUrl} hint="Share this on LinkedIn, Twitter, email signature, anywhere!" />
          <CopyBox label="📅 Direct Booking URL" value={bookingUrl} hint="Link directly to your booking page — highest conversion!" />
          <CopyBox label="🎁 Referral Link (earn ₹500 per signup)" value={referralUrl} hint="When someone signs up and books via this link, you both get ₹500 credit." />

          {/* Tip cards */}
          <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
            {[
              { icon: '✉️', title: 'Email Signature', tip: 'Add your booking link to your work email signature for passive bookings.' },
              { icon: '💼', title: 'LinkedIn Bio', tip: 'Add "Book a 1:1 session with me →" with your link in your LinkedIn About section.' },
              { icon: '📄', title: 'GitHub README', tip: 'Add a DevBrains mentor badge to your GitHub profile README.' },
              { icon: '🌐', title: 'Portfolio Site', tip: 'Embed your mentor card on your personal portfolio website.' },
            ].map(({ icon, title, tip }) => (
              <div key={title} style={{ background: '#f9fafb', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{tip}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SOCIAL POSTS ─────────────────────────────────────── */}
      {activeSection === 'social' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1a1a2e' }}>📲 Ready-to-post Social Content</h3>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, marginTop: 0 }}>Copy any of these posts and share on your social media. They're already written with your details and booking link!</p>

          <SocialPostCard platform="LinkedIn" icon="💼" color="#0A66C2" text={messages.linkedin} url={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`} />
          <SocialPostCard platform="Twitter/X" icon="🐦" color="#1DA1F2" text={messages.twitter} url={`https://twitter.com/intent/tweet?text=${encodeURIComponent(messages.twitter)}`} />
          <SocialPostCard platform="WhatsApp"  icon="💬" color="#25D366" text={messages.whatsapp} url={`https://wa.me/?text=${encodeURIComponent(messages.whatsapp)}`} />
        </div>
      )}

      {/* ── EMBED WIDGET ─────────────────────────────────────── */}
      {activeSection === 'embed' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#1a1a2e' }}>🖥️ Embed Your Mentor Card</h3>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6b7280' }}>Add this card to your portfolio, blog, or any website. Visitors can book directly from there!</p>

          {/* Preview */}
          <div style={{ background: '#f9fafb', border: '1.5px dashed #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxWidth: 380, width: '100%' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#' + (mentor.avatarBg || '1DB954'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#fff', fontSize: 20, flexShrink: 0 }}>
                {mentor.initials}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#1a1a2e' }}>{mentor.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{mentor.role} · {mentor.company}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <span style={{ fontSize: 12, color: '#f59e0b' }}>{'★'.repeat(5)}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{mentor.rating} ({mentor.reviewCount} reviews)</span>
                </div>
              </div>
              <div style={{ background: '#1DB954', color: '#fff', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>Book</div>
            </div>
          </div>

          {/* Code block */}
          <div style={{ position: 'relative' }}>
            <pre style={{ background: '#1a1a2e', color: '#a5f3fc', padding: 16, borderRadius: 10, fontSize: 12, lineHeight: 1.6, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap' }}>
              {embedCode}
            </pre>
            <button onClick={() => { navigator.clipboard?.writeText(embedCode); }} style={{ position: 'absolute', top: 10, right: 10, padding: '5px 12px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
              📋 Copy
            </button>
          </div>
        </div>
      )}

      {/* ── BADGE ─────────────────────────────────────────────── */}
      {activeSection === 'badge' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#1a1a2e' }}>🏷️ DevBrains Mentor Badge</h3>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6b7280' }}>Add this badge to your portfolio, GitHub README, or email footer.</p>

          {/* Badge preview */}
          <div style={{ marginBottom: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a href="#!" onClick={e => e.preventDefault()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1a1a2e', color: '#fff', padding: '10px 18px', borderRadius: 999, textDecoration: 'none', fontFamily: 'sans-serif', fontSize: 14, fontWeight: 600 }}>
              <span style={{ fontSize: 18 }}>🧠</span>
              <span>Mentor on <strong style={{ color: '#1DB954' }}>DevBrains</strong></span>
            </a>
            <a href="#!" onClick={e => e.preventDefault()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1DB954', color: '#fff', padding: '10px 18px', borderRadius: 999, textDecoration: 'none', fontFamily: 'sans-serif', fontSize: 14, fontWeight: 600 }}>
              <span style={{ fontSize: 18 }}>🧠</span>
              <span>Book 1:1 on <strong>DevBrains</strong></span>
            </a>
          </div>

          {/* Badge codes */}
          <CodeBlock label="🔲 HTML Badge (for websites)" code={badgeCode} />
          <CodeBlock label="📝 Markdown Badge (for GitHub README)" code={`[![DevBrains Mentor](https://img.shields.io/badge/DevBrains-Mentor-1DB954?style=for-the-badge&logo=data:image/svg+xml;base64,PHN2Zy8+)](${profileUrl})`} />
        </div>
      )}

      {/* ── STATS ─────────────────────────────────────────────── */}
      {activeSection === 'stats' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1a1a2e' }}>📊 Referral & Share Stats</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { icon: '👥', label: 'Profile Views',       value: '1,247', trend: '+12% this week' },
              { icon: '🔗', label: 'Link Clicks',         value: '389',   trend: '+8% this week' },
              { icon: '📅', label: 'Bookings via Share',  value: '23',    trend: '+3 this week' },
              { icon: '🎁', label: 'Referral Signups',    value: '12',    trend: '+2 this week' },
              { icon: '💰', label: 'Referral Credits',    value: '₹6,000', trend: 'Earned total' },
              { icon: '🔄', label: 'Conversion Rate',     value: '5.9%',  trend: 'Clicks → Bookings' },
            ].map(({ icon, label, value, trend }) => (
              <div key={label} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a2e' }}>{value}</div>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginTop: 2 }}>{label}</div>
                <div style={{ fontSize: 11, color: '#1DB954', marginTop: 4 }}>{trend}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, fontSize: 13, color: '#166534' }}>
            💡 <strong>Pro tip:</strong> Mentors who share their profile at least once a week get <strong>3x more bookings</strong>. Try posting on LinkedIn every Monday with a quick tip!
          </div>
        </div>
      )}

      {showModal && (
        <ShareModal
          mentor={mentor}
          shareUrl={profileUrl}
          referralCode={refCode}
          onClose={() => setShowModal(false)}
          mode="mentor"
        />
      )}
    </div>
  );
}
